import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';

import { api } from '@/lib/api';
import { formatarDataHora } from '@/lib/utils';
import type { RespostaPaginada } from '@/components/paginacao';
import type { RegistroAuditoria } from '@/features/admin/tipos';
import { ROTULOS_PERFIL } from '@/features/auth/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Paginacao } from '@/components/paginacao';
import { Selecao } from '@/components/ui/input';
import { Entrada } from '@/components/ui/input';
import { Etiqueta } from '@/components/ui/badge';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';

const COLUNAS = 5;

/** Ações que merecem destaque visual por indicarem risco de segurança. */
const ACOES_CRITICAS = new Set([
  'auth.login.bloqueado',
  'auth.token.reuso_detectado',
  'usuario.desativado',
]);

function tomDaAcao(acao: string): 'perigo' | 'alerta' | 'neutro' {
  if (ACOES_CRITICAS.has(acao)) return 'perigo';
  if (acao === 'auth.login.falha') return 'alerta';
  return 'neutro';
}

export function PaginaAuditoria() {
  const [pagina, setPagina] = React.useState(1);
  const [acao, setAcao] = React.useState('');
  const [de, setDe] = React.useState('');
  const [ate, setAte] = React.useState('');

  const { data: catalogo } = useQuery({
    queryKey: ['auditoria-catalogo'],
    queryFn: () => api.get<{ acoes: string[]; entidades: string[] }>('/auditoria/catalogo'),
    staleTime: Infinity,
  });

  const parametros = new URLSearchParams({ pagina: String(pagina), limite: '25' });
  if (acao) parametros.set('acao', acao);
  if (de) parametros.set('de', new Date(`${de}T00:00:00`).toISOString());
  if (ate) parametros.set('ate', new Date(`${ate}T23:59:59`).toISOString());

  const { data, isPending } = useQuery({
    queryKey: ['auditoria', pagina, acao, de, ate],
    queryFn: () =>
      api.get<RespostaPaginada<RegistroAuditoria>>(`/auditoria?${parametros.toString()}`),
  });

  return (
    <>
      <CabecalhoPagina
        titulo="Auditoria"
        descricao="Trilha de ações relevantes do sistema. Os registros são apenas inseridos, nunca alterados ou removidos."
      />

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="filtro-acao" className="text-sm font-medium">
            Ação
          </label>
          <Selecao
            id="filtro-acao"
            value={acao}
            onChange={(evento) => {
              setAcao(evento.target.value);
              setPagina(1);
            }}
            className="w-full sm:w-64"
          >
            <option value="">Todas as ações</option>
            {catalogo?.acoes.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Selecao>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filtro-de" className="text-sm font-medium">
            De
          </label>
          <Entrada
            id="filtro-de"
            type="date"
            value={de}
            onChange={(evento) => {
              setDe(evento.target.value);
              setPagina(1);
            }}
            className="w-full sm:w-40"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filtro-ate" className="text-sm font-medium">
            Até
          </label>
          <Entrada
            id="filtro-ate"
            type="date"
            value={ate}
            onChange={(evento) => {
              setAte(evento.target.value);
              setPagina(1);
            }}
            className="w-full sm:w-40"
          />
        </div>
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Data e hora</TabelaTitulo>
            <TabelaTitulo>Ação</TabelaTitulo>
            <TabelaTitulo>Usuário</TabelaTitulo>
            <TabelaTitulo>Entidade</TabelaTitulo>
            <TabelaTitulo>Origem</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={COLUNAS} linhas={8} />}

          {!isPending && data?.dados.length === 0 && (
            <TabelaVazia colunas={COLUNAS}>
              Nenhum registro para os filtros selecionados.
            </TabelaVazia>
          )}

          {data?.dados.map((registro) => (
            <TabelaLinha key={registro.id}>
              <TabelaCelula
                rotulo="Data e hora"
                className="text-silenciado-texto text-xs whitespace-nowrap tabular-nums"
              >
                {formatarDataHora(registro.criadoEm)}
              </TabelaCelula>
              <TabelaCelula rotulo="Ação">
                <Etiqueta tom={tomDaAcao(registro.acao)}>
                  {tomDaAcao(registro.acao) === 'perigo' && (
                    <ShieldCheck className="size-3" aria-hidden="true" />
                  )}
                  {registro.acao}
                </Etiqueta>
              </TabelaCelula>
              <TabelaCelula rotulo="Usuário">
                {registro.usuario ? (
                  <>
                    <div className="text-sm font-medium">{registro.usuario.nome}</div>
                    <div className="text-silenciado-texto text-xs">
                      {ROTULOS_PERFIL[registro.usuario.perfil]}
                    </div>
                  </>
                ) : (
                  <span className="text-silenciado-texto text-sm">Sistema</span>
                )}
              </TabelaCelula>
              <TabelaCelula rotulo="Entidade" className="text-silenciado-texto text-sm">
                {registro.entidade}
              </TabelaCelula>
              <TabelaCelula rotulo="Origem" className="text-silenciado-texto text-xs">
                {registro.ip ?? '—'}
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      {data && <Paginacao meta={data.meta} aoMudarPagina={setPagina} />}
    </>
  );
}
